import { useCallback, useEffect, useState } from "react";
import {
  COMING_SOON,
  CONTINUE_WATCHING,
  GENRES,
  HERO_SLIDES,
  NEW_IN_SUBSCRIPTION,
  TOP_7,
} from "../data/homeData";
import { contentApi, mediaUrl } from "@services/contentApi";

const withCatalogLinks = (items) => items.map((item) => ({ ...item, href: item.href || (item.type === "Сериал" ? "/series" : "/movies") }));

const fallbackHomeData = {
  heroSlides: withCatalogLinks(HERO_SLIDES),
  continueWatching: withCatalogLinks(CONTINUE_WATCHING),
  newInSubscription: withCatalogLinks(NEW_IN_SUBSCRIPTION),
  comingSoon: [],
  topSeven: withCatalogLinks(TOP_7),
  genres: [],
  genreSections: [],
  forYou: [],
};

export default function useHomeData() {
  const [homeData, setHomeData] = useState(fallbackHomeData);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const refreshHomeData = useCallback(async () => {
    setIsLoading(true);

    try {
      const [contents, genres] = await Promise.all([
        contentApi.listContents({ limit: 60 }),
        contentApi.listGenres(),
      ]);
      const backendHomeData = mapContentsToHomeData(contents, genres);
      const nextHomeData = mergeHomeData(fallbackHomeData, backendHomeData);
      setHomeData(nextHomeData);
      setLastUpdatedAt(Date.now());
      return nextHomeData;
    } catch (error) {
      console.warn("Failed to load backend home data. Using fallback data.", error);
      setHomeData(fallbackHomeData);
      return fallbackHomeData;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const runRefresh = async () => {
      if (!isMounted) return;
      await refreshHomeData();
    };

    runRefresh();
    const refreshInterval = window.setInterval(runRefresh, 10 * 60 * 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") runRefresh();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      window.clearInterval(refreshInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshHomeData]);

  return { homeData, isLoading, lastUpdatedAt, refreshHomeData };
}

function mergeHomeData(fallback, backend) {
  return Object.fromEntries(
    Object.entries(fallback).map(([key, value]) => [
      key,
      ["genres", "genreSections", "forYou", "comingSoon"].includes(key) ? (backend[key] || []) : backend[key]?.length ? backend[key] : value,
    ])
  );
}

function mapContentsToHomeData(contents, genres) {
  const visible = Array.isArray(contents)
    ? contents
        .filter(Boolean)
        .filter((item) => item.status === "published")
        .sort(sortContentForHome)
    : [];
  const featured = visible.filter((item) => item.isFeatured);
  const heroSource = (featured.length ? featured : visible).slice(0, 5);
  const comingSoonSource = visible
    .filter((item) => item.releaseDate && new Date(item.releaseDate) > new Date())
    .sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate))
    .slice(0, 12);

  const genreGroups = mapGenreGroups(visible, genres);
  return {
    heroSlides: heroSource.map(toHeroSlide),
    continueWatching: visible.slice(0, 10).map(toContinueWatchingItem),
    newInSubscription: visible.slice(0, 14).map(toMovieCard),
    comingSoon: comingSoonSource.map(toComingSoonItem),
    topSeven: [...visible]
      .sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0))
      .slice(0, 7)
      .map(toTopItem),
    genres: genreGroups.map((group, index) => ({ id: group.id, title: group.title, slug: group.slug, image: GENRES[index % GENRES.length]?.image })),
    genreSections: genreGroups.map((group) => ({ ...group, items: group.contents.map(toMovieCard) })),
    forYou: visible.filter((item) => item.isForYou).map(toMovieCard),
  };
}

function sortContentForHome(a, b) {
  if (Boolean(a.isFeatured) !== Boolean(b.isFeatured)) return a.isFeatured ? -1 : 1;
  if (a.status !== b.status) return a.status === "published" ? -1 : 1;
  return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
}

function mapGenreGroups(contents, genres) {
  return (Array.isArray(genres) ? genres : []).map((genre) => ({
    id: genre._id,
    title: genre.title,
    slug: genre.slug || "",
    contents: contents.filter((item) => (item.genres || []).some((title) => title.toLocaleLowerCase("ru-RU") === genre.title.toLocaleLowerCase("ru-RU"))),
  })).filter((group) => group.contents.length);
}

function contentHref(item) {
  return `/watch/${item.slug || item._id}`;
}

function contentImage(item, preferred = "poster", fallback = "") {
  const source =
    preferred === "backdrop"
      ? item.backdropUrl || item.posterUrl
      : item.posterUrl || item.backdropUrl;
  return source ? mediaUrl(source) : fallback;
}

function contentLogo(item) {
  return item.logoUrl ? mediaUrl(item.logoUrl) : "";
}

function contentYear(item) {
  return item.releaseDate ? new Date(item.releaseDate).getFullYear().toString() : "";
}

function contentType(item) {
  return item.type === "series" ? "Сериал" : "Фильм";
}

function contentRating(item) {
  const rating = Number(item.rating || 0);
  return rating > 0 ? rating.toFixed(1).replace(".", ",") : "";
}

function formatDuration(seconds) {
  const totalMinutes = Math.round(Number(seconds || 0) / 60);
  if (!totalMinutes) return "";

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!hours) return `${minutes} мин`;
  if (!minutes) return `${hours} ч`;
  return `${hours} ч ${minutes} мин`;
}

function formatDay(date) {
  return date ? String(new Date(date).getDate()) : "";
}

function formatMonth(date) {
  if (!date) return "";
  return new Intl.DateTimeFormat("ru-RU", { month: "long" })
    .format(new Date(date))
    .toUpperCase();
}

function toBaseCard(item) {
  return {
    id: item._id,
    href: contentHref(item),
    title: item.title,
    year: contentYear(item),
    type: contentType(item),
    rating: contentRating(item),
  };
}

function toHeroSlide(item, index) {
  return {
    ...toBaseCard(item),
    img: contentImage(item, "backdrop", HERO_SLIDES[index % HERO_SLIDES.length]?.img),
    logo: contentLogo(item),
    description: item.description || "",
  };
}

function toMovieCard(item, index) {
  return {
    ...toBaseCard(item),
    bg: "#111111",
    img: contentImage(item, "poster", NEW_IN_SUBSCRIPTION[index % NEW_IN_SUBSCRIPTION.length]?.img),
  };
}

function toContinueWatchingItem(item, index) {
  return {
    ...toBaseCard(item),
    duration: formatDuration(item.duration),
    progress: [12, 28, 45, 64, 78, 35, 52, 19, 86, 41][index] || 20,
    img: contentImage(item, "backdrop", CONTINUE_WATCHING[index % CONTINUE_WATCHING.length]?.img),
  };
}

function toComingSoonItem(item, index) {
  return {
    ...toBaseCard(item),
    date: formatDay(item.releaseDate),
    month: formatMonth(item.releaseDate),
    img: contentImage(item, "backdrop", COMING_SOON[index % COMING_SOON.length]?.img),
    logo: contentLogo(item),
  };
}

function toTopItem(item, index) {
  return {
    ...toBaseCard(item),
    img: contentImage(item, "poster", TOP_7[index % TOP_7.length]?.img),
  };
}
